export function createProductionLaneRequestRegistry({
  createController = () => new AbortController(),
  trace = () => {},
} = {}) {
  const inFlight = new Map();
  let nextId = 0;

  return {
    request(key, run, metadata = {}) {
      const requestKey = String(key || "");
      const existing = inFlight.get(requestKey);
      if (existing) {
        trace("request-deduplicated", { ...existing.metadata, requestId: existing.id });
        return existing;
      }

      const controller = createController();
      const entry = {
        id: ++nextId,
        metadata,
        key: requestKey,
        controller,
        promise: null,
      };
      entry.promise = Promise.resolve()
        .then(() => {
          trace("request-start", { ...metadata, requestId: entry.id });
          return run(controller.signal);
        })
        .then((result) => {
          trace("request-success", { ...metadata, requestId: entry.id });
          return result;
        }, (error) => {
          trace(error?.name === "AbortError" ? "request-aborted" : "request-failed", {
            ...metadata, requestId: entry.id,
          });
          throw error;
        })
        .finally(() => {
          if (inFlight.get(requestKey) === entry) inFlight.delete(requestKey);
        });
      inFlight.set(requestKey, entry);
      return entry;
    },

    cancelAll(source = "unspecified") {
      for (const entry of inFlight.values()) {
        trace("request-cancel", { ...entry.metadata, requestId: entry.id, source });
        entry.controller?.abort?.();
      }
      inFlight.clear();
    },

    cancelMatching(matches, source) {
      for (const [key, entry] of inFlight) {
        if (!matches(entry.metadata)) continue;
        trace("request-cancel", { ...entry.metadata, requestId: entry.id, source });
        entry.controller?.abort?.();
        inFlight.delete(key);
      }
    },

    has(key) {
      return inFlight.has(String(key || ""));
    },

    async whenIdle() {
      while (inFlight.size) {
        await Promise.allSettled([...inFlight.values()].map((entry) => entry.promise));
      }
    },

    size() {
      return inFlight.size;
    },
  };
}
